import { useState, useRef, useEffect } from 'react'
import { MessageSquare, Trash2, Edit2, Check, X, Sparkles, MessageCircle } from 'lucide-react'
import { cn } from '@/utils'
import type { ConversationSummary } from '@/stores/chatStore'

const CHANNEL_CONFIG: Record<string, { label: string; color: string; icon: typeof MessageSquare }> = {
  copilot: { label: 'Marketing', color: 'text-indigo-500', icon: Sparkles },
  whatsapp: { label: 'WhatsApp', color: 'text-green-500', icon: MessageCircle },
  web: { label: 'Chat', color: 'text-blue-500', icon: MessageSquare },
}

/**
 * Rename and Delete show on hover, on keyboard focus anywhere in the row, and
 * always on a touch screen, which has no hover (the list's drawer below xl).
 */
const REVEAL_ACTION = 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100'

interface ConversationListItemProps {
  conversation: ConversationSummary
  isActive: boolean
  onClick: () => void
  onDelete: () => void
  onRename: (newTitle: string) => void
}

export function ConversationListItem({
  conversation,
  isActive,
  onClick,
  onDelete,
  onRename,
}: ConversationListItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(conversation.title)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Reset edit title when conversation title changes
  useEffect(() => {
    setEditTitle(conversation.title)
  }, [conversation.title])

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const handleSaveEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    const trimmedTitle = editTitle.trim()
    if (trimmedTitle && trimmedTitle !== conversation.title) {
      onRename(trimmedTitle)
    }
    setIsEditing(false)
  }

  const handleCancelEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setEditTitle(conversation.title)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-accent'
      )}
      onClick={isEditing ? undefined : onClick}
      data-testid="conversation-row"
      aria-current={isActive ? 'true' : undefined}
      role="button"
      tabIndex={isEditing ? -1 : 0}
      onKeyDown={(e) => {
        // Only a key on the row itself opens it: Enter or Space on its Rename
        // or Delete button bubbles here and is that button's to handle.
        if (e.target !== e.currentTarget) return
        if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
          onClick()
        }
      }}
    >
      {(() => {
        const channelCfg = CHANNEL_CONFIG[conversation.channel] || CHANNEL_CONFIG.web
        const ChannelIcon = channelCfg.icon
        return <ChannelIcon className={cn('w-4 h-4 shrink-0', channelCfg.color)} />
      })()}

      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'w-full text-sm bg-card px-1.5 py-0.5 rounded-sm border',
              'border-primary focus:outline-hidden focus:ring-1 focus:ring-ring'
            )}
          />
        ) : (
          <>
            <p className="text-sm truncate" title={conversation.title}>
              {conversation.title}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {(() => {
                const channelCfg = CHANNEL_CONFIG[conversation.channel] || CHANNEL_CONFIG.web
                return channelCfg.label
              })()}
              {conversation.personaName && ` · ${conversation.personaName}`}
            </p>
          </>
        )}
      </div>

      {isEditing ? (
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleSaveEdit}
            className={cn(
              'p-1 rounded-sm transition-colors',
              'hover:bg-accent',
              'text-success hover:text-success/80'
            )}
            title="Save"
            aria-label="Save title"
          >
            <Check className="w-3 h-3" />
          </button>
          <button
            onClick={handleCancelEdit}
            className={cn(
              'p-1 rounded-sm transition-colors',
              'hover:bg-accent',
              'text-muted-foreground hover:text-foreground'
            )}
            title="Cancel"
            aria-label="Cancel editing"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleEditClick}
            className={cn(
              REVEAL_ACTION,
              'p-1 rounded-sm transition-opacity',
              'hover:bg-accent',
              'text-muted-foreground hover:text-foreground'
            )}
            title="Rename conversation"
            aria-label="Rename conversation"
          >
            <Edit2 className="w-3 h-3" />
          </button>
          <button
            onClick={handleDelete}
            className={cn(
              REVEAL_ACTION,
              'p-1 rounded-sm transition-opacity',
              'hover:bg-accent',
              'text-muted-foreground hover:text-destructive'
            )}
            title="Delete conversation"
            aria-label="Delete conversation"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  )
}
